import { useContext } from 'react';
import type { ConnectedFacility } from '../../../../../js/api';

import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { Button } from '../../../components/button';
import { DrawerField } from '../../../components/drawer-components';
import { ExpenseAndTermsForm } from '../../../components/expense-and-terms-form';

import { StatusCodes } from '../../../constants/workflows';
import { formatDateTo24Hr } from '../../../utils/date';

type DetailsTabProps = {
  connection: ConnectedFacility;
};

export function DetailsTab({ connection }: DetailsTabProps) {
  const { audienceIsProvider } = useContext(SessionContext);
  const isProvider = audienceIsProvider ?? false;
  const isPendingConfirm = connection.current_status?.code === StatusCodes.PEND_CONFIRM;
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  // Get rates based on user type
  const dayRate = isProvider ? connection.shift_rate_day : connection.facility_rate_day;
  const nightRate = isProvider ? connection.shift_rate_night : connection.facility_rate_night;
  const rate24h = isProvider ? connection.shift_rate_24h : connection.facility_rate_24h;
  const callRate = isProvider ? connection.shift_rate_call : connection.facility_rate_call;

  return (
    <div className="tw:space-y-4">
      <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
        {/* Confirmation Letter */}
        {isPendingConfirm && (
          <DrawerField
            label="Confirmation Letter"
            value={
              <a
                href={`/staff-portal/connections/facility-confirmation-letter/${connection.id}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:inline-block"
              >
                <Button color="brand-green-outline" small>
                  <i className="fas fa-file-pdf" />
                  Generate Confirmation Letter
                </Button>
              </a>
            }
          />
        )}

        {/* Rate Information */}
        <div className="tw:col-span-2">
          <div className="tw:bg-brand-green-3 tw:rounded-md tw:mt-3 tw:p-2">
            <div className="tw:text-sm tw:font-medium tw:mb-1">Hourly Rates</div>
            <div className="tw:grid tw:grid-cols-4 tw:gap-1">
              {!!dayRate && dayRate !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(dayRate)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Daytime</div>
                </div>
              )}
              {!!nightRate && nightRate !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(nightRate)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Overnight</div>
                </div>
              )}
              {!!rate24h && rate24h !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(rate24h)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">24 hour</div>
                </div>
              )}
              {!!callRate && callRate !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(callRate)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Call</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dates */}
        <DrawerField
          label="Created"
          value={
            connection.connected_at
              ? formatDateTo24Hr(connection.connected_at, 'MM/dd/yyyy HH:mm')
              : null
          }
        />
        <DrawerField
          label="Last Updated"
          value={
            connection.updated_at
              ? formatDateTo24Hr(connection.updated_at, 'MM/dd/yyyy HH:mm')
              : null
          }
        />
      </div>

      {/* Expenses & Terms */}
      <ExpenseAndTermsForm
        entity={connection}
        entityType="connection"
        onUpdate={() => {
          // ExpenseAndTermsForm handles its own query invalidation
        }}
      />
    </div>
  );
}
